import {forwardRef, type MouseEventHandler, type ReactNode, useCallback, useImperativeHandle, useRef} from 'react';
import {type ToggleStateOptions, useToggleState} from '@react-stately/toggle';
import {useHover} from '@react-aria/interactions';
import {useButton, type AriaButtonProps} from '@react-aria/button';
import classnames from 'classnames';
import {FocusRing} from '@react-aria/focus';
import {ChevronDownIcon} from '@time-webkit/all/icons';

import {useOnClickOutside} from 'usehooks-ts';
import {type FocusableElement} from '@react-types/shared';

import styles from './styles.module.css';

type ButtonProps = ToggleStateOptions & AriaButtonProps & {
    onPress?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

type Props = {
    id: string;
    activeIcon: ReactNode;
    inactiveIcon?: ReactNode;
    leftButtonProps?: ButtonProps;
    rightButtonProps?: ButtonProps;
    shouldCloseOnClickOutside?: boolean;
}

type Refs = {
    leftButton: HTMLButtonElement | null;
    rightButton: HTMLButtonElement | null;
}

const defaultButtonProps: ButtonProps = {
    type: 'button',
};

export const SplitButton = forwardRef<Refs, Props>(({
    activeIcon,
    inactiveIcon = activeIcon,
    leftButtonProps = defaultButtonProps,
    rightButtonProps = defaultButtonProps,
    shouldCloseOnClickOutside = true,
}, ref) => {
    const leftButtonRef = useRef<HTMLButtonElement>(null);
    const rightButtonRef = useRef<HTMLButtonElement>(null);
    const leftButtonState = useToggleState(leftButtonProps);
    const rightButtonState = useToggleState(rightButtonProps);

    const leftOnPress = useCallback(() => {
        leftButtonProps.onPress?.();
        leftButtonState.toggle();
    }, [leftButtonProps, leftButtonState]);

    const rightOnPress = useCallback(() => {
        rightButtonProps.onPress?.();
        rightButtonState.toggle();
    }, [rightButtonProps, rightButtonState]);

    const {isPressed: isLeftButtonPressed} = useButton({...leftButtonProps}, leftButtonRef);
    const {isPressed: isRightButtonPressed} = useButton({...rightButtonProps}, rightButtonRef);

    const {
        hoverProps: leftButtonHoverProps,
        isHovered: isLeftButtonHovered,
    } = useHover({isDisabled: leftButtonProps.isDisabled});
    const {
        hoverProps: rightButtonHoverProps,
        isHovered: isRightButtonHovered,
    } = useHover({isDisabled: rightButtonProps.isDisabled});

    const leftOnMouseEnter: MouseEventHandler<FocusableElement> = useCallback((event) => {
        leftButtonHoverProps.onMouseEnter?.(event);
        leftButtonProps.onMouseEnter?.();
    }, [leftButtonHoverProps, leftButtonProps]);

    const leftOnMouseLeave: MouseEventHandler<FocusableElement> = useCallback((event) => {
        leftButtonHoverProps.onMouseLeave?.(event);
        leftButtonProps.onMouseLeave?.();
    }, [leftButtonHoverProps, leftButtonProps]);

    const rightOnMouseEnter: MouseEventHandler<FocusableElement> = useCallback((event) => {
        rightButtonHoverProps.onMouseEnter?.(event);
        rightButtonProps.onMouseEnter?.();
    }, [rightButtonHoverProps, rightButtonProps]);

    const rightOnMouseLeave: MouseEventHandler<FocusableElement> = useCallback((event) => {
        rightButtonHoverProps.onMouseLeave?.(event);
        rightButtonProps.onMouseLeave?.();
    }, [rightButtonHoverProps, rightButtonProps]);

    useImperativeHandle(ref, () => ({
        leftButton: leftButtonRef.current,
        rightButton: rightButtonRef.current,
    }));

    useOnClickOutside(rightButtonRef, () => {
        if (shouldCloseOnClickOutside && rightButtonState.isSelected) {
            rightOnPress();
        }
    });

    return (
        <div
            className={classnames(styles.buttonGroup, {
                [styles.buttonGroupHovered]: isLeftButtonHovered || isRightButtonHovered,
                [styles.buttonGroupPressed]: isLeftButtonPressed || isRightButtonPressed,
            })}
        >
            <FocusRing focusRingClass={styles.focused}>
                <button
                    {...leftButtonHoverProps}
                    onClick={leftOnPress}
                    onMouseEnter={leftOnMouseEnter}
                    onMouseLeave={leftOnMouseLeave}
                    ref={leftButtonRef}
                    className={classnames(styles.leftButton, {
                        [styles.leftButtonActive]: leftButtonState.isSelected,
                        [styles.leftButtonHovered]: isLeftButtonHovered,
                        [styles.leftButtonPressed]: isLeftButtonPressed,
                    })}
                >
                    {leftButtonState.isSelected ? activeIcon : inactiveIcon}
                </button>
            </FocusRing>
            <FocusRing focusRingClass={styles.focused}>
                <button
                    {...rightButtonHoverProps}
                    onClick={rightOnPress}
                    onMouseEnter={rightOnMouseEnter}
                    onMouseLeave={rightOnMouseLeave}
                    ref={rightButtonRef}
                    className={styles.rightButton}
                >
                    <ChevronDownIcon
                        className={classnames(styles.rightButtonIcon, {
                            [styles.rightButtonIconActive]: rightButtonState.isSelected,
                            [styles.rightButtonHovered]: isRightButtonHovered,
                            [styles.rightButtonPressed]: isRightButtonPressed,
                        })} width={20} height={20}
                    />
                </button>
            </FocusRing>
        </div>
    );
});
